import { useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, MenuItem, Select, TextField, Typography } from "@mui/material";
import { formatCurrency } from "../../utils/formatCurrency";
import { EMPTY_ADDRESS, addressIncomplete } from "../../utils/returnAddress";
import { OfferBody, MAX_TEXT_LENGTH } from "./useInquiryDetail";
import ReturnAddressFields from "./ReturnAddressFields";
import DialogSuccess from "./DialogSuccess";
import { BUTTON, DIALOG, FIELD, FIELD_LABEL, INK, OUTLINED } from "./styles";

type Props = {
  open: boolean;
  onClose: () => void;
  amount: number;
  currency: string;
  offerTypes: string[];
  onSubmit: (body: OfferBody) => Promise<boolean>;
  submitting: boolean;
  error: string | null;
};

const OFFER_TYPE_LABELS: Record<string, string> = {
  REFUND: "Partial refund, keep the item",
  REFUND_WITH_RETURN: "Full refund after return",
};

const SHORTCUTS: [number, string][] = [[30, "30%"], [50, "50%"], [70, "70%"], [100, "Full"]];

export default function MakeOfferDialog({ open, onClose, amount, currency, offerTypes, onSubmit, submitting, error }: Props) {
  const [offerType, setOfferType] = useState(offerTypes[0] ?? '');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [done, setDone] = useState(false);
  const cents = Math.round(parseFloat(value) * 100);
  const withAddress = offerType === 'REFUND_WITH_RETURN';
  const addressMissing = withAddress && addressIncomplete(address);
  const noteOver = note.length > MAX_TEXT_LENGTH;
  const blocked = submitting || !(cents > 0) || cents > amount || !note.trim() || noteOver || addressMissing;

  const pctCents = (pct: number) => Math.round(amount * pct / 100);

  const setPct = (pct: number) => setValue((pctCents(pct) / 100).toFixed(2));

  const submit = async () => {
    if (await onSubmit({ offerType, amount: cents, note, ...(withAddress && { returnAddress: address }), idempotencyKey })) setDone(true);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, why) => { if (!submitting && (done || why !== 'backdropClick')) onClose(); }}
      maxWidth={false}
      aria-labelledby="offer-dialog-title"
      slotProps={{ paper: { sx: DIALOG } }}
    >
      {done ? (
        <DialogSuccess titleId="offer-dialog-title" title="Offer sent" detail="Waiting for the buyer to answer." onClose={onClose} />
      ) : (
        <>
      <Box>
        <Typography id="offer-dialog-title" component="h2" sx={{ fontSize: 18, fontWeight: 600 }}>Make an offer</Typography>
        <Typography sx={{ fontSize: 13, color: INK.muted, mt: '4px' }}>
          PayPal sends it to the buyer. If they accept, PayPal refunds the amount from the account balance and closes the inquiry.
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
        <Box>
          <Box component="label" id="offer-type-label" sx={FIELD_LABEL}>Offer</Box>
          <Select size="small" disabled={submitting} fullWidth labelId="offer-type-label" value={offerType} onChange={(e) => setOfferType(e.target.value)} sx={{ fontSize: 13, borderRadius: '8px' }}>
            {offerTypes.map((type) => <MenuItem key={type} value={type} sx={{ fontSize: 13 }}>{OFFER_TYPE_LABELS[type] ?? type}</MenuItem>)}
          </Select>
        </Box>
        <Box>
          <Box component="label" htmlFor="offer-amount" sx={FIELD_LABEL}>Amount</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              id="offer-amount"
              disabled={submitting}
              type="number"
              size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
              sx={{ ...FIELD, width: 110, flexShrink: 0 }}
            />
            <Box component="span" sx={{ fontSize: 13, color: INK.muted, whiteSpace: 'nowrap' }}>
              {currency.toUpperCase()} of {formatCurrency(amount, currency)}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {SHORTCUTS.map(([pct, label]) => {
          const on = value !== '' && cents === pctCents(pct);
          return (
            <Button
              key={pct}
              disabled={submitting}
              variant="outlined"
              size="small"
              sx={{ ...OUTLINED, ...(on && { color: INK.primary, borderColor: INK.primary }) }}
              onClick={() => setPct(pct)}
            >
              {label} · {formatCurrency(pctCents(pct), currency)}
            </Button>
          );
        })}
      </Box>
      <Box>
        <Box component="label" htmlFor="offer-note" sx={FIELD_LABEL}>Message to the buyer (sent with the offer)</Box>
        <TextField
          id="offer-note"
          disabled={submitting}
          fullWidth
          multiline
          minRows={5}
          maxRows={9}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ ...FIELD, '& .MuiInputBase-root': { ...FIELD['& .MuiInputBase-root'], lineHeight: 1.5 } }}
        />
        <Box sx={{ fontSize: 12, color: noteOver ? INK.danger : INK.muted, textAlign: 'right', mt: '4px' }}>
          {note.length.toLocaleString('en-US')} / {MAX_TEXT_LENGTH.toLocaleString('en-US')}
        </Box>
      </Box>
      {withAddress && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
          <ReturnAddressFields value={address} onChange={setAddress} disabled={submitting} />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" sx={OUTLINED} disabled={submitting} onClick={onClose}>Cancel</Button>
        <Button variant="contained" size="small" sx={BUTTON} disabled={blocked} onClick={submit}>
          {submitting ? <><CircularProgress size={14} color="inherit" sx={{ mr: '8px' }} />Sending offer…</> : "Send offer"}
        </Button>
      </Box>
        </>
      )}
    </Dialog>
  );
}
